import React from 'react';
import { IconSaxIcon } from '@/components/design-system/atoms/icon/iconsax-icon';
import { getDiscountCategoryLabel } from '../utils';
import type { Discount } from '../../../types/discount';
import { useTranslation } from 'react-i18next';

type DiscountTableProps = {
  discounts: Discount[];
  onViewDiscount: (discount: Discount) => void;
  onDeleteDiscount: (discount: Discount) => void;
  contractFullRent: number;
};

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric' };
  return date.toLocaleDateString('en-GB', options);
};

const DiscountTable: React.FC<DiscountTableProps> = ({
  discounts,
  onViewDiscount,
  onDeleteDiscount,
  contractFullRent,
}) => {
  const { t } = useTranslation();
  const totalDiscounts = discounts.reduce((sum, d) => sum + d.amount_cents, 0) / 100;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full gap-y-3">
        <thead>
          <tr>
            <th className="width-[230px] px-3 text-left text-[12px] font-medium tracking-wider text-[#6C757D]">
              {t('js.features.admin.contracts.discount_table.columns.type') as string}
            </th>
            <th className="width-[230px] px-3 text-left text-[12px] font-medium tracking-wider text-[#6C757D]">
              {t('js.features.admin.contracts.discount_table.columns.period') as string}
            </th>
            <th className="width-[230px] px-3 text-left text-[12px] font-medium tracking-wider text-[#6C757D]">
              {t('js.features.admin.contracts.discount_table.columns.start_date') as string}
            </th>
            <th className="width-[230px] px-3 text-left text-[12px] font-medium tracking-wider text-[#6C757D]">
              {t('js.features.admin.contracts.discount_table.columns.end_date') as string}
            </th>
            <th className="px-3 text-center text-[12px] font-medium tracking-wider text-[#6C757D]">
              {' '}
            </th>
            <th className="px-3 text-right text-[12px] font-medium tracking-wider text-[#6C757D]">
              {t('js.features.admin.contracts.discount_table.columns.amount') as string}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={6} style={{ height: '12px', background: 'transparent' }}></td>
          </tr>
          {discounts.map((discount, idx) => (
            <React.Fragment key={discount.id}>
              <tr>
                <td
                  className="whitespace-nowrap bg-N100 p-3 align-middle text-[14px] font-medium text-N900"
                  style={{
                    borderTopLeftRadius: '16px',
                    borderBottomLeftRadius: '16px',
                  }}
                >
                  {getDiscountCategoryLabel(discount.discount_category)}
                </td>
                <td className="whitespace-nowrap bg-N100 p-3 align-middle text-[14px] font-medium text-N900">
                  <div className="flex flex-col gap-1">
                    <span>
                      {discount.period === 'recurring'
                        ? (t('js.features.admin.contracts.discount_table.periods.recurring') as string)
                        : discount.period === 'next_month'
                        ? (t('js.features.admin.contracts.discount_table.periods.next_month') as string)
                        : (t(
                            'js.features.admin.contracts.discount_table.periods.this_month'
                          ) as string)}
                    </span>
                    {(discount.period === 'this_month' || discount.period === 'next_month') && discount.date && (
                      <span className="text-md text-gray-500">{formatDate(discount.date)}</span>
                    )}
                  </div>
                </td>
                <td className="whitespace-nowrap bg-N100 p-3 align-middle text-[14px] font-medium text-N900">
                  {formatDate(discount.start_date)}
                </td>
                <td className="whitespace-nowrap bg-N100 p-3 align-middle text-[14px] font-medium text-N900">
                  {formatDate(discount.end_date)}
                </td>
                <td className="whitespace-nowrap bg-N100 p-3 text-center">
                  <span className="inline-flex items-center gap-4">
                    <IconSaxIcon
                      name="eye"
                      size={24}
                      strokeColor="#1A1A1A"
                      className="cursor-pointer"
                      onClick={() => onViewDiscount(discount)}
                    />
                    <IconSaxIcon
                      name="trash"
                      size={24}
                      strokeColor="#E5422F"
                      className="cursor-pointer"
                      onClick={() => onDeleteDiscount(discount)}
                    />
                    <span
                      className="ml-2 inline-block rounded-full px-2 py-1 text-[12px] font-medium shadow"
                      style={{
                        minWidth: '22px',
                        textAlign: 'center',
                        background:
                          'linear-gradient(22.08deg, #F71818 -22.56%, #F9441B -9.98%, #FB7B1E 14.62%, #FEC600 58.21%, #FECA06 66.97%, #FED50E 75.17%, #FEE916 83.15%, #FFFF25 89.78%)',
                      }}
                    >
                      10%
                    </span>
                  </span>
                </td>
                <td
                  className="whitespace-nowrap bg-N100 p-3 text-right align-middle text-[14px] font-bold"
                  style={{
                    borderTopRightRadius: '16px',
                    borderBottomRightRadius: '16px',
                  }}
                >
                  -€{(discount.amount_cents / 100).toFixed(2)}
                </td>
              </tr>
              {idx < discounts.length - 1 && (
                <tr>
                  <td colSpan={6} style={{ height: '12px', background: 'transparent' }}></td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={5} className="bg-white px-0 py-4 text-[14px] font-bold">
              {t('js.features.admin.contracts.discount_table.summary.total_discounts') as string}
            </td>
            <td className="text-N900 bg-white px-0 py-4 text-right text-[14px] font-bold">
              -€{totalDiscounts.toFixed(2)}
            </td>
          </tr>
          <tr>
            <td
              colSpan={6}
              className="p-0"
              style={{ height: '8px', background: 'transparent' }}
            ></td>
          </tr>
          <tr>
            <td colSpan={5} className="bg-white px-0 py-4 align-middle text-[16px] font-bold">
              {t('js.features.admin.contracts.discount_table.summary.month_rent') as string}
            </td>
            <td className="bg-white px-0 py-4 text-right align-middle text-[16px] font-bold">
              <span className="mr-2 text-[14px] font-normal text-gray-400 line-through">
                (€{(contractFullRent + totalDiscounts).toFixed(2)})
              </span>
              €{contractFullRent.toFixed(2)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default DiscountTable;
